import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Search, Briefcase, MapPin, Calendar, Mail, Phone,
  Send, FileText, Plus, Building, RefreshCw, XCircle, Pause,
  Bell, CheckCircle, Clock, User, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const STATUTS_EMPLOI = {
  "en_recherche": { label: "En recherche", icon: Search, color: "#3B82F6", highlight: true },
  "en_emploi": { label: "En emploi", icon: Briefcase, color: "#22C55E", highlight: false },
  "autre_emploi": { label: "Autre emploi", icon: RefreshCw, color: "#8B5CF6", highlight: false },
  "arret": { label: "Arrêt", icon: Pause, color: "#EF4444", highlight: false },
};

const TYPES_CONTRAT = ["CDI", "CDD", "Saisonnier", "Stage", "Alternance"];

const MoniteursPage = ({ user }) => {
  const [moniteurs, setMoniteurs] = useState([]);
  const [offres, setOffres] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showOffreDialog, setShowOffreDialog] = useState(false);
  const [showCandidatureDialog, setShowCandidatureDialog] = useState(false);
  const [selectedMoniteur, setSelectedMoniteur] = useState(null);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [sending, setSending] = useState(false);
  
  const [messageData, setMessageData] = useState({
    sujet: '',
    contenu: ''
  });
  
  const [offreData, setOffreData] = useState({
    titre: '',
    description: '',
    lieu: '',
    type_contrat: 'CDI',
    date_debut: '',
    salaire: '',
    contact_email: user?.email || '',
    contact_telephone: ''
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [moniteursRes, offresRes] = await Promise.all([
        axios.get('/moniteurs'),
        axios.get('/offres-emploi')
      ]);
      setMoniteurs(moniteursRes.data);
      setOffres(offresRes.data);
      
      // Charger les candidatures pour toutes les offres de l'entreprise
      const allCandidatures = [];
      for (const offre of offresRes.data) {
        try {
          const candRes = await axios.get(`/offres-emploi/${offre.offre_id}/candidatures`);
          allCandidatures.push(...candRes.data.map(c => ({
            ...c,
            offre_titre: offre.titre,
            offre_lieu: offre.lieu
          })));
        } catch (e) {
          // Ignorer les erreurs
        }
      }
      setCandidatures(allCandidatures);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredMoniteurs = moniteurs.filter(m => {
    const matchSearch = searchTerm === '' || 
      `${m.prenom} ${m.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.ville?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatut = filterStatut === 'all' || m.statut_emploi === filterStatut;
    return matchSearch && matchStatut;
  });

  const handleContact = (moniteur) => {
    setSelectedMoniteur(moniteur);
    setMessageData({ sujet: '', contenu: '' });
    setShowContactDialog(true);
  };

  const sendMessage = async () => {
    if (!messageData.sujet || !messageData.contenu) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`/messages/moniteur/${selectedMoniteur.user_id}`, messageData);
      toast.success('Message envoyé !');
      setShowContactDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const createOffre = async () => {
    if (!offreData.titre || !offreData.description || !offreData.lieu) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    setSending(true);
    try {
      await axios.post('/offres-emploi', offreData);
      toast.success('Offre publiée !');
      setShowOffreDialog(false);
      loadData();
      setOffreData({
        titre: '',
        description: '',
        lieu: '',
        type_contrat: 'CDI',
        date_debut: '',
        salaire: '',
        contact_email: user?.email || '',
        contact_telephone: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    } finally {
      setSending(false);
    }
  };

  const deleteOffre = async (offreId) => {
    if (!window.confirm('Supprimer cette offre ?')) return;
    try {
      await axios.delete(`/offres-emploi/${offreId}`);
      toast.success('Offre supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleViewCandidature = (candidature) => {
    setSelectedCandidature(candidature);
    setShowCandidatureDialog(true);
  };

  const updateCandidatureStatut = async (candidatureId, newStatut) => {
    try {
      await axios.put(`/candidatures/${candidatureId}/statut`, { statut: newStatut });
      toast.success('Statut mis à jour');
      setShowCandidatureDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getCandidatureStatutBadge = (statut) => {
    const styles = {
      en_attente: { bg: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'En attente' },
      acceptee: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Acceptée' },
      refusee: { bg: 'bg-red-100 text-red-800', icon: XCircle, label: 'Refusée' },
      entretien: { bg: 'bg-blue-100 text-blue-800', icon: Calendar, label: 'Entretien' }
    };
    const style = styles[statut] || styles.en_attente;
    const Icon = style.icon;
    return (
      <Badge className={style.bg}>
        <Icon className="h-3 w-3 mr-1" />
        {style.label}
      </Badge>
    );
  };

  const candidaturesEnAttente = candidatures.filter(c => c.statut === 'en_attente').length;

  const getStatutBadge = (statut) => {
    const info = STATUTS_EMPLOI[statut] || STATUTS_EMPLOI.en_recherche;
    const Icon = info.icon;
    return (
      <Badge 
        style={{ backgroundColor: info.color }} 
        className={`text-white ${info.highlight ? 'animate-pulse ring-2 ring-blue-300' : ''}`}
      >
        <Icon className="h-3 w-3 mr-1" />
        {info.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-[#8DC63F]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="moniteurs-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Moniteurs VNM diplômés</h1>
          <p className="text-slate-500">Trouvez des moniteurs qualifiés pour votre structure</p>
        </div>
        <Button onClick={() => setShowOffreDialog(true)} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
          <Plus className="h-4 w-4 mr-2" />
          Publier une offre
        </Button>
      </div>

      <Tabs defaultValue="moniteurs">
        <TabsList>
          <TabsTrigger value="moniteurs">Moniteurs ({moniteurs.length})</TabsTrigger>
          <TabsTrigger value="offres">Mes offres ({offres.length})</TabsTrigger>
          <TabsTrigger value="candidatures" className="relative">
            Réponses aux offres ({candidatures.length})
            {candidaturesEnAttente > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                {candidaturesEnAttente}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="moniteurs" className="space-y-4">
          {/* Filtres */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom ou ville..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Statut emploi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_recherche">En recherche</SelectItem>
                <SelectItem value="en_emploi">En emploi</SelectItem>
                <SelectItem value="autre_emploi">Autre emploi</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Liste moniteurs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredMoniteurs.map(moniteur => (
              <Card key={moniteur.user_id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{moniteur.prenom} {moniteur.nom}</h3>
                      <p className="text-sm text-slate-500">Diplômé {moniteur.annee_diplome}</p>
                    </div>
                    {getStatutBadge(moniteur.statut_emploi)}
                  </div>
                  
                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    {moniteur.ville && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-slate-400" />
                        {moniteur.ville}
                      </div>
                    )}
                    {moniteur.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-400" />
                        {moniteur.email}
                      </div>
                    )}
                    {moniteur.telephone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {moniteur.telephone}
                      </div>
                    )}
                    {moniteur.emploi_structure && (
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-slate-400" />
                        {moniteur.emploi_structure}
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => handleContact(moniteur)}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Contacter
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredMoniteurs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Aucun moniteur trouvé</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="offres" className="space-y-4">
          {offres.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 mb-4">Vous n'avez pas encore publié d'offres</p>
                <Button onClick={() => setShowOffreDialog(true)} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
                  Publier ma première offre
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {offres.map(offre => (
                <Card key={offre.offre_id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{offre.titre}</h3>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {offre.lieu}
                          </span>
                          <Badge variant="outline">{offre.type_contrat}</Badge>
                          {offre.salaire && <span>{offre.salaire}</span>}
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => deleteOffre(offre.offre_id)}
                      >
                        Supprimer
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600 mt-3">{offre.description}</p>
                    <p className="text-xs text-slate-400 mt-2">
                      Publiée le {new Date(offre.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="candidatures" className="space-y-4">
          {candidatures.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Bell className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 mb-4">Aucune candidature reçue pour le moment</p>
                <p className="text-sm text-slate-400">Les moniteurs VNM peuvent postuler à vos offres d'emploi</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Candidatures en attente en premier */}
              {candidatures
                .sort((a, b) => {
                  if (a.statut === 'en_attente' && b.statut !== 'en_attente') return -1;
                  if (a.statut !== 'en_attente' && b.statut === 'en_attente') return 1;
                  return new Date(b.created_at) - new Date(a.created_at);
                })
                .map(candidature => (
                <Card 
                  key={candidature.candidature_id} 
                  className={`transition-all ${candidature.statut === 'en_attente' ? 'border-l-4 border-l-yellow-500 bg-yellow-50/50' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="h-10 w-10 rounded-full bg-[#8DC63F]/20 flex items-center justify-center">
                            <User className="h-5 w-5 text-[#8DC63F]" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">
                              {candidature.moniteur_prenom} {candidature.moniteur_nom}
                            </h3>
                            <p className="text-sm text-slate-500">
                              Pour : {candidature.offre_titre}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-3">
                          {candidature.moniteur_ville && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-4 w-4 text-slate-400" />
                              {candidature.moniteur_ville}
                            </span>
                          )}
                          {candidature.moniteur_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-4 w-4 text-slate-400" />
                              {candidature.moniteur_email}
                            </span>
                          )}
                          {candidature.moniteur_telephone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-4 w-4 text-slate-400" />
                              {candidature.moniteur_telephone}
                            </span>
                          )}
                          {candidature.moniteur_annee_diplome && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4 text-slate-400" />
                              Diplômé {candidature.moniteur_annee_diplome}
                            </span>
                          )}
                        </div>

                        {candidature.commentaire && (
                          <div className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700 mb-3">
                            <p className="font-medium text-xs text-slate-500 mb-1">Message du candidat :</p>
                            {candidature.commentaire}
                          </div>
                        )}

                        <p className="text-xs text-slate-400">
                          Reçue le {new Date(candidature.created_at).toLocaleDateString('fr-FR')} à {new Date(candidature.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
                        </p>
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        {getCandidatureStatutBadge(candidature.statut)}
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewCandidature(candidature)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Détails
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog Contact */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Contacter {selectedMoniteur?.prenom} {selectedMoniteur?.nom}</DialogTitle>
            <DialogDescription>
              Envoyez un message à ce moniteur
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Sujet</Label>
              <Input
                value={messageData.sujet}
                onChange={(e) => setMessageData({...messageData, sujet: e.target.value})}
                placeholder="Ex: Opportunité d'emploi..."
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={messageData.contenu}
                onChange={(e) => setMessageData({...messageData, contenu: e.target.value})}
                placeholder="Votre message..."
                rows={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>Annuler</Button>
            <Button onClick={sendMessage} disabled={sending} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
              {sending ? 'Envoi...' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Offre */}
      <Dialog open={showOffreDialog} onOpenChange={setShowOffreDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Publier une offre d'emploi</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Titre du poste *</Label>
              <Input
                value={offreData.titre}
                onChange={(e) => setOffreData({...offreData, titre: e.target.value})}
                placeholder="Ex: Moniteur jet-ski saison 2026"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={offreData.description}
                onChange={(e) => setOffreData({...offreData, description: e.target.value})}
                placeholder="Décrivez le poste, les missions..."
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lieu *</Label>
                <Input
                  value={offreData.lieu}
                  onChange={(e) => setOffreData({...offreData, lieu: e.target.value})}
                  placeholder="Ville"
                />
              </div>
              <div>
                <Label>Type de contrat</Label>
                <Select value={offreData.type_contrat} onValueChange={(v) => setOffreData({...offreData, type_contrat: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_CONTRAT.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={offreData.date_debut}
                  onChange={(e) => setOffreData({...offreData, date_debut: e.target.value})}
                />
              </div>
              <div>
                <Label>Salaire (optionnel)</Label>
                <Input
                  value={offreData.salaire}
                  onChange={(e) => setOffreData({...offreData, salaire: e.target.value})}
                  placeholder="Ex: 1800€ net"
                />
              </div>
            </div>
            <div>
              <Label>Email de contact</Label>
              <Input
                type="email"
                value={offreData.contact_email}
                onChange={(e) => setOffreData({...offreData, contact_email: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOffreDialog(false)}>Annuler</Button>
            <Button onClick={createOffre} disabled={sending} className="bg-[#8DC63F] hover:bg-[#7AB62F]">
              {sending ? 'Publication...' : 'Publier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Candidature */}
      <Dialog open={showCandidatureDialog} onOpenChange={setShowCandidatureDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-[#8DC63F]" />
              Candidature de {selectedCandidature?.moniteur_prenom} {selectedCandidature?.moniteur_nom}
            </DialogTitle>
            <DialogDescription>
              Pour l'offre : {selectedCandidature?.offre_titre}
            </DialogDescription>
          </DialogHeader>
          
          {selectedCandidature && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Statut actuel :</span>
                {getCandidatureStatutBadge(selectedCandidature.statut)}
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <h4 className="font-medium text-sm">Informations du candidat</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {selectedCandidature.moniteur_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <a href={`mailto:${selectedCandidature.moniteur_email}`} className="text-blue-600 hover:underline">
                        {selectedCandidature.moniteur_email}
                      </a>
                    </div>
                  )}
                  {selectedCandidature.moniteur_telephone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <a href={`tel:${selectedCandidature.moniteur_telephone}`} className="text-blue-600 hover:underline">
                        {selectedCandidature.moniteur_telephone}
                      </a>
                    </div>
                  )}
                  {selectedCandidature.moniteur_ville && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {selectedCandidature.moniteur_ville}
                    </div>
                  )}
                  {selectedCandidature.moniteur_annee_diplome && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      Diplômé {selectedCandidature.moniteur_annee_diplome}
                    </div>
                  )}
                </div>
              </div>

              {selectedCandidature.commentaire && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-sm text-blue-800 mb-2">Message du candidat</h4>
                  <p className="text-sm text-blue-900">{selectedCandidature.commentaire}</p>
                </div>
              )}

              {selectedCandidature.moniteur_cv && (
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <a 
                    href={selectedCandidature.moniteur_cv} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Voir le CV
                  </a>
                </div>
              )}

              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Changer le statut :</p>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm"
                    variant={selectedCandidature.statut === 'en_attente' ? 'default' : 'outline'}
                    onClick={() => updateCandidatureStatut(selectedCandidature.candidature_id, 'en_attente')}
                    className={selectedCandidature.statut === 'en_attente' ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    En attente
                  </Button>
                  <Button 
                    size="sm"
                    variant={selectedCandidature.statut === 'entretien' ? 'default' : 'outline'}
                    onClick={() => updateCandidatureStatut(selectedCandidature.candidature_id, 'entretien')}
                    className={selectedCandidature.statut === 'entretien' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Entretien
                  </Button>
                  <Button 
                    size="sm"
                    variant={selectedCandidature.statut === 'acceptee' ? 'default' : 'outline'}
                    onClick={() => updateCandidatureStatut(selectedCandidature.candidature_id, 'acceptee')}
                    className={selectedCandidature.statut === 'acceptee' ? 'bg-green-500 hover:bg-green-600' : ''}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Accepter
                  </Button>
                  <Button 
                    size="sm"
                    variant={selectedCandidature.statut === 'refusee' ? 'default' : 'outline'}
                    onClick={() => updateCandidatureStatut(selectedCandidature.candidature_id, 'refusee')}
                    className={selectedCandidature.statut === 'refusee' ? 'bg-red-500 hover:bg-red-600' : ''}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Refuser
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCandidatureDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MoniteursPage;
